import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AdminTeamDTO } from "@shaarp/common";
import { format } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { HiOutlineX } from "react-icons/hi";
import { ButtonLoading } from "../button-loading";
import { Button } from "../ui/button";
import { DialogFooter, DialogHeader } from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

export const DialogAdminTeamTrial = ({
  team,
  open,
  onClose,
}: {
  team: AdminTeamDTO;
  open: boolean;
  onClose: () => void;
}) => {
  const toastError = useToastError();
  const { toast } = useToast();
  const api = useApi();
  const { t, currentLanguage } = useTranslation();
  const { refreshTeams } = useAdminTeamsEmitter();

  const [trialType, setTrialType] = useState<string>(
    team.trialCountDays ? "onboarding" : team.trialEndsAt ? "trial" : "none"
  );
  const [trialCountDays, setTrialCountDays] = useState<number>(
    team.trialCountDays || 7
  );
  const [trialPaymentMethodRequired, setTrialPaymentMethodRequired] =
    useState<boolean>(team.paymentMethodRequired);
  const [trialEndsAt, setTrialEndsAt] = useState<Date | undefined>(
    team.trialEndsAt ? new Date(team.trialEndsAt) : undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const save = async () => {
    let params: Record<string, any> = {
      trialType,
    };

    if (trialType === "none") {
      params = {
        ...params,
        trialCountDays: null,
        trialPaymentMethodRequired: null,
        trialEndsAt: null,
      };
    } else if (trialType === "onboarding") {
      params = {
        ...params,
        trialCountDays,
        trialPaymentMethodRequired,
        trialEndsAt: null,
      };
    } else if (trialType === "trial") {
      const trialEndsDay = trialEndsAt
        ? new Date(trialEndsAt.setHours(23, 59, 59, 999))
        : null;

      params = {
        ...params,
        trialCountDays: null,
        trialPaymentMethodRequired: null,
        trialEndsAt: trialEndsDay,
      };
    }

    try {
      setIsLoading(true);
      await api.post(`/admin/team/${team.id}/trial`, params);
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.dialogs.trial.successTitle"),
        description: t(
          "admin.dataTableAdminTeams.dialogs.trial.successDescription"
        ),
      });
      refreshTeams();
    } catch (e) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.dataTableAdminTeams.dialogs.trial.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-5 border-t">
          <div className="col-span-1">
            <div className="font-bold">
              {t("admin.dataTableAdminTeams.dialogs.trial.type")}
            </div>
            <div>
              <Select value={trialType} onValueChange={setTrialType}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("admin.dataTableAdminTeams.dialogs.trial.typeNone")}
                  </SelectItem>
                  <SelectItem value="onboarding">
                    {t(
                      "admin.dataTableAdminTeams.dialogs.trial.typeOnboarding"
                    )}
                  </SelectItem>
                  <SelectItem value="trial">
                    {t("admin.dataTableAdminTeams.dialogs.trial.typeUntilDate")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {trialType === "onboarding" && (
            <>
              <div className="col-span-1">
                <div className="font-bold">
                  {t(
                    "admin.dataTableAdminTeams.dialogs.trial.daysAfterValidation"
                  )}
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder={t(
                      "admin.dataTableAdminTeams.dialogs.trial.daysPlaceholder"
                    )}
                    className="w-full"
                    value={trialCountDays}
                    onChange={(e) =>
                      setTrialCountDays(parseInt(e.target.value))
                    }
                  />
                </div>
              </div>
              <div className="col-span-1">
                <div className="font-bold">
                  {t(
                    "admin.dataTableAdminTeams.dialogs.trial.paymentMethodRequired"
                  )}
                </div>
                <div>
                  <Switch
                    checked={trialPaymentMethodRequired}
                    onCheckedChange={() =>
                      setTrialPaymentMethodRequired(!trialPaymentMethodRequired)
                    }
                  />
                </div>
              </div>
            </>
          )}
          {trialType === "trial" && (
            <div className="col-span-1">
              <div className="font-bold">
                {t("admin.dataTableAdminTeams.dialogs.trial.endDate")}
              </div>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-[280px] justify-start text-left font-normal",
                        !trialEndsAt && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 w-4 h-4" />
                      {trialEndsAt ? (
                        format(trialEndsAt, "PPP", {
                          locale: currentLanguage === "fr" ? fr : enUS,
                        })
                      ) : (
                        <span>
                          {t(
                            "admin.dataTableAdminTeams.dialogs.trial.selectDate"
                          )}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto">
                    <Calendar
                      mode="single"
                      selected={trialEndsAt || undefined}
                      onSelect={(date) => setTrialEndsAt(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button
                  variant={"outline"}
                  size={"icon"}
                  onClick={() => setTrialEndsAt(undefined)}
                >
                  <HiOutlineX className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <ButtonLoading onClick={save} isLoading={isLoading}>
            {t("admin.dataTableAdminTeams.dialogs.trial.save")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
